import * as React from 'react'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import { green } from '@mui/material/colors'
import Button from '@mui/material/Button'
import Fab from '@mui/material/Fab'
import { useTheme } from '@mui/material/styles'
import GradientCircularProgressIndicator from '../GradientCircularProgressIndicator'

export default function AsynchronousActionButton({
    onClick,
    children,
    disabled = false,
    sx = {},
    startIcon = null,
    endIcon = null,
    loading = false,
    success = false,
    setSuccess,
    setLoading,
    fullWidth = false,
    variant = 'contained',
}) {
    const theme = useTheme()
    const buttonRef = React.useRef(null)
    const [progressSize, setProgressSize] = React.useState(24)

    const buttonSx = {
        ...(success && {
            bgcolor: theme.palette.success.main,
            '&:hover': {
                bgcolor: theme.palette.success.light,
            },
        }),
    }

    // Measure button height and calculate progress indicator size
    React.useEffect(() => {
        if (!buttonRef.current) return

        const updateProgressSize = () => {
            if (buttonRef.current) {
                const buttonHeight = buttonRef.current.offsetHeight
                // Set progress size to 60% of button height
                const newSize = Math.round(buttonHeight * 0.6)
                setProgressSize(newSize)
            }
        }

        // Initial measurement
        updateProgressSize()

        // Set up ResizeObserver for dynamic resizing
        const resizeObserver = new ResizeObserver(updateProgressSize)
        resizeObserver.observe(buttonRef.current)

        return () => {
            resizeObserver.disconnect()
        }
    }, [sx, children]) // Re-measure if sx or children change

    const handleButtonClick = async () => {
        if (!loading && onClick) {
            // Don't set loading here - let the parent component handle it
            // This allows the parent to control the loading state based on actual async operations
            try {
                await onClick()
            } catch (error) {
                console.error('Error in async button click:', error)
                // Parent should handle error state
            }
        }
    }

    // Extract positioning-related styles from sx to apply to the outer Box
    const { position, top, right, bottom, left, marginLeft, marginRight, marginTop, marginBottom, zIndex, transform, ...buttonStyles } = sx || {}
    
    const boxPositioningStyles = {
        ...(position && { position }),
        ...(top !== undefined && { top }),
        ...(right !== undefined && { right }),
        ...(bottom !== undefined && { bottom }),
        ...(left !== undefined && { left }),
        ...(marginLeft !== undefined && { marginLeft }),
        ...(marginRight !== undefined && { marginRight }),
        ...(marginTop !== undefined && { marginTop }),
        ...(marginBottom !== undefined && { marginBottom }),
        ...(zIndex !== undefined && { zIndex }),
        ...(transform !== undefined && { transform }),
    }

    return (
        <Box sx={{ 
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            ...boxPositioningStyles,
        }}>
            <Button
                fullWidth={fullWidth}
                ref={buttonRef}
                startIcon={startIcon}
                endIcon={endIcon}
                variant={variant}
                sx={{
                    ...buttonSx,
                    ...buttonStyles,
                }}
                disabled={loading || disabled}
                onClick={handleButtonClick}
            >
                {children}
            </Button>
            {loading && (
                <GradientCircularProgressIndicator 
                    size={progressSize}
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        marginLeft: `-${progressSize / 2}px`,
                        marginTop: `-${progressSize / 2}px`,
                    }}
                />
            )}
        </Box>
    )
}